import type { XAxisProps, YAxisProps } from "recharts";
import {
  CartesianGrid,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { DARK_BLUE, PURPLE } from "theme/colors";

import type { TooltipContentProps } from "../TooltipContent";
import { TooltipContent } from "../TooltipContent";
import { DEFAULT_HEIGHT } from "../utils";

type LineConfig = {
  color: string;
  dataKey: string;
  name: string;
};

interface LineChartProps {
  data: unknown[];
  height?: number;
  lines: LineConfig[];
  tooltipProps?: TooltipContentProps;
  xAxisProps?: Omit<XAxisProps, "ref">;
  yAxisProps?: Omit<YAxisProps, "ref">;
}

export const LineChart: React.FC<LineChartProps> = ({ data, height, lines, tooltipProps, xAxisProps, yAxisProps }) => {
  return (
    <ResponsiveContainer width="100%" height={height || DEFAULT_HEIGHT}>
      <RechartsLineChart
        data={data}
        margin={{
          top: 20,
          right: 8,
          left: 0,
          bottom: 5,
        }}
      >
        <CartesianGrid vertical={false} stroke={DARK_BLUE[20]} />
        <XAxis
          dataKey={xAxisProps?.dataKey}
          fontSize={14}
          tick={{ fill: DARK_BLUE[80] }}
          tickLine={false}
          fontWeight={500}
          stroke={DARK_BLUE[20]}
          {...xAxisProps}
        />
        <YAxis
          dataKey={yAxisProps?.dataKey}
          tick={{ fill: DARK_BLUE[80] }}
          axisLine={false}
          tickLine={false}
          fontSize="14px"
          fontWeight={500}
          allowDecimals={false}
          {...yAxisProps}
        />
        <Tooltip
          content={tooltipProps ? <TooltipContent {...tooltipProps} /> : undefined}
          cursor={data && data?.length > 0 ? { fill: PURPLE[20], fillOpacity: 0.25 } : false}
        />
        {lines.map(line => (
          <Line
            key={line.dataKey}
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.color}
            type="monotone"
            dot={{
              fill: line.color,
            }}
            activeDot={{ r: 8 }}
          />
        ))}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
};
